import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import "@/App.css";
import "@/styles/inputs.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MagicLogin from "@/pages/MagicLogin";
import Settings from "@/pages/Settings";
import ClientDashboard from "@/pages/ClientDashboard";
import AgentDashboard from "@/pages/AgentDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUserDetail from "@/pages/AdminUserDetail";
import NewTask from "@/pages/NewTask";
import TaskDetail from "@/pages/TaskDetail";

function AuthCallback() {
  const navigate = useNavigate();
  const { exchangeGoogleSession } = useAuth();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(m[1]);
    const role = sessionStorage.getItem("eb_google_role") || "client";
    (async () => {
      try {
        const user = await exchangeGoogleSession(sessionId, role);
        // Clean hash
        window.history.replaceState(null, "", window.location.pathname);
        if (user.role === "agent") navigate("/agent", { replace: true });
        else if (user.role === "admin") navigate("/admin", { replace: true });
        else navigate("/dashboard", { replace: true });
      } catch (e) {
        navigate("/login?google=failed", { replace: true });
      }
    })();
  }, [exchangeGoogleSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center text-[var(--eb-text-dim)]">
      <div className="eb-fadein">Signing you in…</div>
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--eb-text-dim)]">
        <div className="eb-fadein">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    if (user.role === "client") return <Navigate to="/dashboard" replace />;
    if (user.role === "agent") return <Navigate to="/agent" replace />;
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

function RootRouter() {
  const location = useLocation();
  // Process Google session_id during render (before other routing logic)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/magic-login" element={<MagicLogin />} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute roles={["client"]}><ClientDashboard /></ProtectedRoute>} />
      <Route path="/agent" element={<ProtectedRoute roles={["agent"]}><AgentDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users/:userId" element={<ProtectedRoute roles={["admin"]}><AdminUserDetail /></ProtectedRoute>} />
      <Route path="/tasks/new" element={<ProtectedRoute roles={["client"]}><NewTask /></ProtectedRoute>} />
      <Route path="/tasks/:id" element={<ProtectedRoute><TaskDetail /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" theme="light" toastOptions={{ style: { background: "#FFFFFF", color: "#14201A", border: "1px solid #E5DDCC", boxShadow: "0 10px 24px -12px rgba(14,122,61,0.18)" } }} />
        <RootRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
